import { useState, useEffect, useContext, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { alertActions } from '../../store/alert';
import TimeCardAddItem from './TimecardAddItem';
import TimecardItem from './TimecardItem';
import TimecardDialog from './TimecardDialog';
import DualListBox from './DualListBox';
import CopyPreviousEntryDialog from './CopyPreviousEntryDialog';
import AlertDialog from '../utilities/AlertDialog';
import { AuthContext } from '../context/auth-context';
import TimecardsAPI from '../../services/api/timecards/timecards-api';
import EmployeesAPI from '../../services/api/admin/employees-api';
import PackagesAPI from '../../services/api/admin/packages-api';
import { getApiClient } from '../../services/api/axios-config';
import Logger from '../../utils/logger';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import CloseIcon from '@mui/icons-material/Close';
import {
  DataGrid,
  GridActionsCellItem,
  GridFooter,
  GridFooterContainer,
} from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import WarningIcon from '@mui/icons-material/Warning';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  format,
  isSunday,
  nextSunday,
  isSaturday,
  previousFriday,
  subDays,
  addDays,
} from 'date-fns';

export const Timecard = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('delete');
  const [currentTimeItem, setCurrentTimeItem] = useState({
    project_name: '',
    package_name: '',
    activity_name: '',
    hours_st: 0,
    hours_ot: 0,
    hours_dt: 0,
    exception: '',
  });
  const [activityOptions, setActivityOptions] = useState([]);
  const [activePackages, setActivePackages] = useState([]);
  const [filteredActivePackages, setFilteredActivePackages] = useState([]); // Packages filtered by employee location
  const [activeProjects, setActiveProjects] = useState([]);
  const [areas, setAreas] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [timeCardUser, setTimeCardUser] = useState('');
  const [timeCardForeman, setTimeCardForeman] = useState('');
  const [crewRefreshTrigger, setCrewRefreshTrigger] = useState(0);
  const [timecards, setTimecards] = useState([]);
  const [timecardsWeek, setTimecardsWeek] = useState([]);
  const [timeCardDate, setTimeCardDate] = useState(
    format(new Date(), 'yyyy/MM/dd')
  );
  const [weekEnd, setWeekEnd] = useState(
    isSunday(new Date())
      ? format(new Date(), 'yyyy/MM/dd')
      : format(nextSunday(new Date(timeCardDate)), 'yyyy/MM/dd')
  );
  const [weekStart, setWeekStart] = useState(
    isSaturday(new Date()) || isSunday(new Date())
      ? format(previousFriday(subDays(new Date(timeCardDate), 4)), 'yyyy/MM/dd')
      : format(previousFriday(new Date(timeCardDate)), 'yyyy/MM/dd')
  );
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [employmentStatusModalOpen, setEmploymentStatusModalOpen] =
    useState(false);
  const [employmentStatuses, setEmploymentStatuses] = useState([]);
  const [selectedEmploymentStatus, setSelectedEmploymentStatus] = useState('');
  const [employmentStatusChangeDate, setEmploymentStatusChangeDate] = useState(
    new Date()
  );
  const [isUpdatingEmploymentStatus, setIsUpdatingEmploymentStatus] =
    useState(false);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [copyPreviousEntryModalOpen, setCopyPreviousEntryModalOpen] =
    useState(false);
  const pendingStatusUpdateRef = useRef(null); // Store pending status update to preserve optimistic update
  const dualListBoxRef = useRef(null);

  // Union override state
  const [baseUnion, setBaseUnion] = useState('');
  const [temporaryWorkingUnion, setTemporaryWorkingUnion] = useState(null);
  const [effectiveUnion, setEffectiveUnion] = useState('');
  const [sessionUnion, setSessionUnion] = useState(null);
  const [isSessionOverride, setIsSessionOverride] = useState(false);
  const [unionOverrideType, setUnionOverrideType] = useState('date_only'); // "date_only" or "persistent"
  const [unionInputValue, setUnionInputValue] = useState('');

  const [employeeIsShopLocation, setEmployeeIsShopLocation] = useState(false);
  const [isLocationOverrideActive, setIsLocationOverrideActive] =
    useState(false);
  const [locationOverrideConfirmOpen, setLocationOverrideConfirmOpen] =
    useState(false);

  const auth = useContext(AuthContext);

  const dispatch = useDispatch();

  // requestConfig removed - using API services instead

  // this is for the notes icon on the datagrid
  const renderDetailsButton = (params) => {
    if (params.row.notes) {
      return (
        <Tooltip title={params.row.notes} placement="top-start" arrow>
          <StickyNote2OutlinedIcon
            color="action"
            onClick={() => {
              handleEditNote(params.row);
            }}
          />
        </Tooltip>
      );
    }
  };

  const getPackageDisplayName = (row) => {
    if (row.autodesk_name?.trim()) {
      return `${row.autodesk_name} - ${row.package_name}`;
    }
    return row.package_name || '';
  };

  const getWorkItem = (params) => {
    return `${params.row.project} /${getPackageDisplayName(params.row)} /${params.row.activity_name}`;
  };

  const columns = [
    {
      field: 'work_item',
      headerName: 'Work Item',
      flex: 1,
      valueGetter: getWorkItem,
    },
    {
      field: 'project',
      headerName: 'Project',
      flex: 1,
    },
    {
      field: 'package_name',
      headerName: 'Package',
      flex: 2,
      valueGetter: (params) => getPackageDisplayName(params.row),
    },
    {
      field: 'activity_name',
      headerName: 'Activity',
      flex: 0.5,
    },
    {
      field: 'location_type',
      headerName: 'Type',
      flex: 0.3,
      renderCell: (params) => {
        const locationType = params.row.location_type;
        if (!locationType) {
          return null;
        }
        const isShop = locationType.toLowerCase() === 'shop';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              label={isShop ? 'SHOP' : 'FIELD'}
              size="small"
              color={isShop ? 'warning' : 'primary'}
              sx={{ fontWeight: 'bold', fontSize: '0.7rem', height: 22 }}
            />
            {params.row.is_location_override && (
              <Tooltip title="Shop-to-field location override">
                <WarningIcon sx={{ color: '#ff9800', fontSize: 18 }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'hours_st',
      headerName: 'ST',
      flex: 0.1,
      editable: true,
    },
    {
      field: 'hours_ot',
      headerName: 'OT',
      flex: 0.1,
      editable: true,
    },
    {
      field: 'hours_dt',
      headerName: 'DT',
      flex: 0.1,
      editable: true,
    },
    {
      field: 'shift',
      headerName: 'Shift',
      flex: 0.1,
      valueGetter: (params) => params.row.shift || '',
    },
    {
      field: 'exception',
      headerName: 'Exception',
      flex: 0.5,
      editable: true,
    },
    {
      field: 'union',
      headerName: 'Union',
      flex: 0.5,
      renderCell: (params) => {
        if (params.row.is_union_override) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2">
                {params.row.union || 'N/A'}
              </Typography>
              <Tooltip title="Union Override Active">
                <WarningIcon sx={{ color: '#ff9800', fontSize: 18 }} />
              </Tooltip>
            </Box>
          );
        }
        return (
          <Typography variant="body2">{params.row.union || 'N/A'}</Typography>
        );
      },
    },
    {
      field: 'notes',
      headerName: '',
      align: 'right',
      renderCell: renderDetailsButton,
      flex: 0.1,
    },
    {
      field: 'actions',
      type: 'actions',
      flex: 0.3,
      headerName: '',
      cellClassName: 'actions',
      getActions: (row) => {
        // Show lock icon for locked entries
        if (row.row.is_locked) {
          return [
            <Tooltip title="Cannot edit locked timecards" placement="bottom">
              <span>
                <GridActionsCellItem
                  icon={<LockIcon />}
                  label="Locked"
                  disabled
                />
              </span>
            </Tooltip>,
          ];
        }
        // Show normal edit/delete buttons for unlocked entries
        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            name="edit"
            label="Edit"
            onClick={() => {
              handleEdit(row.row);
            }}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon color="action" />}
            label="Delete"
            onClick={() => {
              handleConfirmDelete(row.row);
            }}
            color="inherit"
          />,
        ];
      },
    },
  ];

  const CustomFooter = () => {
    return (
      <GridFooterContainer>
        <Box
          sx={{
            justifyContent: 'left',
            display: 'flex',
          }}
        >
          Total Hours:{'  '}
          {timecards.reduce((accumulator, object) => {
            return accumulator + object.hours_st;
          }, 0)}
          {'  '}
          ST{'  '}
          {timecards.reduce((accumulator, object) => {
            return accumulator + object.hours_ot;
          }, 0)}
          {'  '}
          OT{'  '}
          {timecards.reduce((accumulator, object) => {
            return accumulator + object.hours_dt;
          }, 0)}
          {'  '}
          DT
        </Box>
        <GridFooter
          sx={{
            border: 'none', // To delete double border.
            justifyContent: 'left',
          }}
        />
      </GridFooterContainer>
    );
  };

  // this only runs at the beginning of loading the timecard and gets all the info.
  useEffect(() => {
    const getTimecardOptions = async () => {
      try {
        const options = await TimecardsAPI.getTimecardOptions(auth.token);
        const usersSent = options.timecardUsers;
        setActivityOptions(options.activityOptions);
        setActivePackages(options.activePackages);
        setFilteredActivePackages(options.activePackages); // Initialize with all packages
        setActiveProjects(options.activeProjects);
        setUsers(usersSent);

        // Filter foremen who are active and have foreman role
        const foremanUsers = usersSent.filter(
          (user) =>
            user.is_timecard === true &&
            (user.employment_status_id === 'active' ||
              !user.employment_status_id)
        );

        // Default to the logged-in user if they are a foreman
        const loggedInUserEmployeeId = auth.employeeId || auth.userId;
        const loggedInForeman = foremanUsers.find(
          (u) => u.id === loggedInUserEmployeeId
        );

        if (loggedInForeman) {
          setTimeCardForeman(loggedInForeman);
        } else if (foremanUsers.length === 1) {
          setTimeCardForeman(foremanUsers[0]);
        }
        Logger.debug('Timecard information loaded', {
          options,
          sampleUserStatusId: usersSent[0]?.employment_status_id,
        });

        // Areas will be fetched when a project is selected in TimecardAddItem
        // Areas are now project-specific and require a projectId
        setAreas([]);

        return;
      } catch (err) {
        dispatch(
          alertActions.showAlert({
            title: 'Error Getting Timecard Info',
            message:
              'The following error occured while retrieving the timecard data: ' +
              err.message,
          })
        );
        Logger.error('Error getting timecard data', err);
      }
    };
    if (auth.token) {
      getTimecardOptions();
    }
  }, [auth.token, auth.employeeId, auth.userId, dispatch]);

  // Fetch employment statuses
  useEffect(() => {
    const getEmploymentStatuses = async () => {
      try {
        const statuses = await EmployeesAPI.getEmploymentStatuses(auth.token);
        setEmploymentStatuses(statuses);
      } catch (err) {
        Logger.error('Error getting employment statuses', err);
      }
    };
    if (auth.token) {
      getEmploymentStatuses();
    }
  }, [auth.token]);

  // Filter users based on selected foreman
  useEffect(() => {
    const getCrewForForeman = async () => {
      if (timeCardForeman?.id && auth.token) {
        try {
          const crewMembers = await EmployeesAPI.getForemanAssigned(
            timeCardForeman.id,
            auth.token
          );

          // Include the foreman themselves in the dropdown
          const usersToDisplay = [timeCardForeman, ...crewMembers];
          setFilteredUsers(usersToDisplay);

          // Clear selected employee if they're not in the new crew (including foreman)
          // But don't clear if we're in the middle of updating employment status
          if (
            timeCardUser &&
            !usersToDisplay.find((u) => u.id === timeCardUser.id) &&
            !isUpdatingEmploymentStatus
          ) {
            setTimeCardUser('');
          }
        } catch (err) {
          console.error('Error getting crew for foreman:', err);
          setFilteredUsers([timeCardForeman]); // At least show the foreman
        }
      } else {
        // No foreman selected, show all users
        setFilteredUsers(users);
      }
    };

    getCrewForForeman();
  }, [
    timeCardForeman,
    auth.token,
    users,
    crewRefreshTrigger,
    isUpdatingEmploymentStatus,
    timeCardUser,
  ]);

  // Restore user from filteredUsers when updating employment status
  useEffect(() => {
    if (
      isUpdatingEmploymentStatus &&
      pendingUserId &&
      filteredUsers.length > 0
    ) {
      // Find the exact user object from filteredUsers to ensure reference match
      const userToRestore = filteredUsers.find((u) => u.id === pendingUserId);
      if (userToRestore) {
        // Check if we have a pending status update to preserve
        const pendingStatusUpdate = pendingStatusUpdateRef.current;

        // Merge backend user with optimistic status update if needed
        const finalUserToRestore = pendingStatusUpdate
          ? {
              ...userToRestore,
              // Preserve optimistic status if backend doesn't match yet
              employment_status_id:
                userToRestore.employment_status_id ===
                pendingStatusUpdate.selectedStatus
                  ? userToRestore.employment_status_id
                  : pendingStatusUpdate.selectedStatus,
              last_status_change_date:
                userToRestore.employment_status_id ===
                pendingStatusUpdate.selectedStatus
                  ? userToRestore.last_status_change_date
                  : pendingStatusUpdate.changeDate,
            }
          : userToRestore;

        // Clear the ref
        pendingStatusUpdateRef.current = null;

        // Ensure we're using the exact object reference from filteredUsers
        // Use requestAnimationFrame to ensure Select has updated its options
        requestAnimationFrame(() => {
          setTimeCardUser(finalUserToRestore);
          setPendingUserId(null);
          setIsUpdatingEmploymentStatus(false);
        });
      } else {
        // If user not found in filteredUsers, clear the ref and selection
        pendingStatusUpdateRef.current = null;
        setTimeCardUser('');
        setPendingUserId(null);
        setIsUpdatingEmploymentStatus(false);
      }
    }
  }, [filteredUsers, isUpdatingEmploymentStatus, pendingUserId]);

  // this will get the new timecards anytime the user or the week is changed
  useEffect(() => {
    const getTimeCards = async () => {
      let timecardRequest;
      try {
        const responseData = await TimecardsAPI.getUserTimeRange(
          timeCardUser.id,
          weekStart,
          weekEnd,
          auth.token
        );

        timecardRequest = responseData.timecard_items;
        Logger.debug('Timecard line items for the week', {
          count: timecardRequest?.length,
          weekStart,
          weekEnd,
        });
        setTimecardsWeek(timecardRequest);

        const currentTimeItems = timecardRequest.filter(
          (timeItem) =>
            format(new Date(timeItem.date), 'yyyy/MM/dd') === timeCardDate
        );

        Logger.debug('Current date time items', {
          count: currentTimeItems?.length,
          date: timeCardDate,
        });
        setTimecards(currentTimeItems);
        setIsLoading(false);
        return;
      } catch (err) {
        Logger.error('Error getting the timecard', err);
        dispatch(
          alertActions.showAlert({
            title: 'Error Getting Timecards',
            message:
              'The following error occured when getting the new timecards: ' +
              err.message,
          })
        );
        return;
      }
    };

    if (timeCardUser) {
      setIsLoading(true);
      getTimeCards();
    }
  }, [weekEnd, timeCardUser, timeCardDate, weekStart, auth.token, dispatch]);

  // this adds multiple new line items to the timecard
  const addTimeItems = async (newTimecards) => {
    Logger.userAction('Adding time items', { count: newTimecards?.length });

    const newItems = [];
    const duplicates = [];
    const archivedPackages = [];

    // Process each new timecard
    for (const newTimecard of newTimecards) {
      if (newTimecard.package?.archived) {
        archivedPackages.push(newTimecard.package.package_name);
        continue;
      }
      const time_id = `${timeCardDate.replaceAll('/', '-')}/${
        timeCardUser.lastname
      }:${timeCardUser.firstname}/${newTimecard.project.number}/${
        newTimecard.package.package_id
      }/${newTimecard.activity.activity_name}`;

      // Check against existing timecards AND newly added items in this batch
      const existsInState = timecards.some((e) => e.id === time_id);
      const existsInBatch = newItems.some((e) => e.id === time_id);

      if (existsInState || existsInBatch) {
        duplicates.push(newTimecard);
        continue;
      }

      let notes = null;
      // Use payroll notes if provided
      if (newTimecard.payrollNotes && newTimecard.payrollNotes.trim() !== '') {
        notes = newTimecard.payrollNotes.trim();
      }

      // Determine which union to use
      // If union is provided in newTimecard (e.g., from copying previous day), use it
      // Otherwise, calculate based on current state like addTimeItem does
      // Use effectiveUnion which already accounts for temporary overrides
      // Fallback to user's home_union from users array if needed
      const userHomeUnion =
        timeCardUser?.home_union ||
        users.find((u) => u.id === timeCardUser?.id)?.home_union ||
        '';
      const unionToUse =
        newTimecard.union !== undefined && newTimecard.union !== null
          ? newTimecard.union
          : isSessionOverride && sessionUnion
            ? sessionUnion
            : effectiveUnion ||
              temporaryWorkingUnion ||
              baseUnion ||
              userHomeUnion ||
              '';

      // Check if union differs from base union
      // If is_union_override is provided, use it; otherwise calculate
      const isUnionOverride =
        newTimecard.is_union_override !== undefined
          ? newTimecard.is_union_override
          : unionToUse !== baseUnion;

      const newItem = {
        id: time_id,
        date: timeCardDate,
        employee_id: timeCardUser.id,
        foreman_id: timeCardForeman?.id || null,
        project_id: newTimecard.project.id,
        project: `${newTimecard.project.number} - ${newTimecard.project.name}`,
        project_number: newTimecard.project.number,
        project_name: newTimecard.project.name,
        package_id: newTimecard.package.package_id,
        package_name: newTimecard.package.package_name,
        autodesk_name: newTimecard.package.autodesk_name || null,
        area_id: newTimecard.package.area_id,
        activity_id: newTimecard.activity.activity_id,
        activity_name: newTimecard.activity.activity_name,
        hours_st: Number(newTimecard.hours_st),
        hours_ot: Number(newTimecard.hours_ot),
        hours_dt: Number(newTimecard.hours_dt),
        shift: newTimecard.shift || null,
        exception: newTimecard.exception || null,
        notes: notes,
        union: unionToUse || null,
        is_union_override: isUnionOverride,
        location_override_requested: isLocationOverrideActive,
      };

      newItems.push(newItem);
    }

    if (archivedPackages.length > 0) {
      dispatch(
        alertActions.showAlert({
          title: 'Archived Packages Skipped',
          message: `The following packages are archived and were not copied: ${archivedPackages.join(
            ', '
          )}.`,
        })
      );
    }

    if (newItems.length === 0) {
      if (duplicates.length > 0) {
        Logger.debug('All items were duplicates, skipping add');
      }
      return; // Nothing to add
    }

    // Update state with all new items
    // We can just append them or try to insert them in order.
    // For simplicity and batching, we'll append them.
    // But wait, existing logic tries to insert them intelligently.
    // Let's simplify: just append them for now, or insert them one by one into a copy.

    let newTimecardsDay = [...timecards];
    let newTimecardsWeek = [...timecardsWeek];

    // Insert each item
    for (const item of newItems) {
      // If index is -1 (not found), splice at -1 would act like array.length - 1, which is wrong.
      // Splice at -1 removes/inserts at last element.
      // If lastIndexOf returns -1, we should push to end?
      // Original code: if -1, it splices at -1? Wait, splice(-1, 0, item) inserts before the last element.
      // Let's check original code behavior: "return -1"
      // splice(-1, 0, item) -> inserts at index len-1.
      // If we want to append, we should use push or splice(len, 0).
      // But the original code used splice with the result of lastIndexOf.
      // If lastIndexOf returns -1, splice(-1, 0, item) inserts at the end in some browsers or before last in others?
      // Actually: splice(start, deleteCount, item). If start is negative, it's from end.
      // If we want to insert at end when not found, we should use length.
      // Let's stick to a simpler approach: just append for batch add to avoid complexity,
      // OR fix the index logic properly.

      // Let's just push for now to ensure they get added.
      newTimecardsDay.push(item);
      newTimecardsWeek.push(item);
    }

    setTimecards(newTimecardsDay);
    setTimecardsWeek(newTimecardsWeek);

    try {
      // Send all items to the backend at once
      const apiClient = getApiClient(auth.token);
      const response = await apiClient.post('/api/timecards/addtimecarditem', {
        timecard_item: newItems, // Send all items as an array
      });
      const serverTimecards = response.data.data.addedTimecards || [];
      Logger.info('Timecards added successfully', {
        count: serverTimecards?.length,
      });

      if (serverTimecards.length > 0) {
        const resolvedById = new Map(
          serverTimecards.map((tc) => [
            tc.id,
            {
              location_type: tc.location_type,
              is_location_override: tc.is_location_override,
              phase: tc.phase,
              cost_code: tc.cost_code,
            },
          ])
        );
        const applyResolved = (rows) =>
          rows.map((tc) =>
            resolvedById.has(tc.id) ? { ...tc, ...resolvedById.get(tc.id) } : tc
          );
        setTimecards((prev) => applyResolved(prev));
        setTimecardsWeek((prev) => applyResolved(prev));
      }
    } catch (err) {
      Logger.error('Error adding timecards', err);

      let errorMessage;
      if (err.code === 'ECONNABORTED') {
        errorMessage =
          'The server did not respond.  Please check your network connection and try again';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message?.includes('Network Error')) {
        errorMessage =
          'Could not connect to the server. Check your internet connection.';
      } else {
        errorMessage = 'An unknown error occured.';
      }

      dispatch(
        alertActions.showAlert({
          title: 'Error Adding Timecards',
          message:
            'The server encountered the following error while adding the timecards: ' +
            errorMessage,
        })
      );

      setTimeCardUser('');
    }
  };

  // this adds a new line item to the timecard
  const addTimeItem = async (newTimecard) => {
    Logger.userAction('Adding timecard item', { timecard: newTimecard });

    if (newTimecard.package?.archived) {
      dispatch(
        alertActions.showAlert({
          title: 'Package is Archived',
          message: `Package "${newTimecard.package.package_name}" is archived and cannot receive new time entries.`,
        })
      );
      return;
    }
    const time_id = `${timeCardDate.replaceAll('/', '-')}/${
      timeCardUser.lastname
    }:${timeCardUser.firstname}/${newTimecard.project.number}/${
      newTimecard.package.package_id
    }/${newTimecard.activity.activity_name}`;

    if (timecards.filter((e) => e.id === time_id).length > 0) {
      dispatch(
        alertActions.showAlert({
          title: 'Item Already Exists',
          message:
            'This item already exists in the timecard.  Please update the values in the current item',
        })
      );
      return;
    }

    let notes = null;

    // Use payroll notes if provided
    if (newTimecard.payrollNotes && newTimecard.payrollNotes.trim() !== '') {
      notes = newTimecard.payrollNotes.trim();
    }

    // this checks if the project is a note project and adds the day as a note
    // If payroll notes exist, combine with the day note
    if (newTimecard.project.number.slice(0, 1) === 'A') {
      const weekday = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
      const d = new Date(timeCardDate);
      const dayNote = weekday[d.getDay()];
      if (notes) {
        notes = `${notes} / ${dayNote}`;
      } else {
        notes = dayNote;
      }
    }

    // Determine which union to use
    // Use effectiveUnion which already accounts for temporary overrides
    // Fallback to user's home_union from users array if needed
    const userHomeUnion =
      timeCardUser?.home_union ||
      users.find((u) => u.id === timeCardUser?.id)?.home_union ||
      '';
    const unionToUse =
      isSessionOverride && sessionUnion
        ? sessionUnion
        : effectiveUnion ||
          temporaryWorkingUnion ||
          baseUnion ||
          userHomeUnion ||
          '';

    // Check if union differs from base union
    const isUnionOverride = unionToUse !== baseUnion;

    const newItem = {
      id: time_id,
      date: timeCardDate,
      employee_id: timeCardUser.id,
      foreman_id: timeCardForeman?.id || null,
      project_id: newTimecard.project.id,
      project: `${newTimecard.project.number} - ${newTimecard.project.name}`,
      project_number: newTimecard.project.number,
      project_name: newTimecard.project.name,
      package_id: newTimecard.package.package_id,
      package_name: newTimecard.package.package_name,
      autodesk_name: newTimecard.package.autodesk_name || null,
      area_id: newTimecard.package.area_id,
      activity_id: newTimecard.activity.activity_id,
      activity_name: newTimecard.activity.activity_name,
      hours_st: Number(newTimecard.hours_st),
      hours_ot: Number(newTimecard.hours_ot),
      hours_dt: Number(newTimecard.hours_dt),
      shift: newTimecard.shift || null,
      exception: newTimecard.exception || null,
      notes: notes,
      union: unionToUse || null,
      is_union_override: isUnionOverride,
      location_override_requested: isLocationOverrideActive,
    };

    // This function calculates the correct index to insert the new time item in the timecards for the day and for the week.
    const lastIndexOf = (array, packageId, projectId) => {
      for (let i = array.length - 1; i >= 0; i--) {
        if (array[i].package_id === packageId) return i + 1;
      }
      for (let i = array.length - 1; i >= 0; i--) {
        if (array[i].project_id === projectId) return i + 1;
      }
      return -1;
    };

    const indexInsertDay = lastIndexOf(
      timecards,
      newTimecard.package.package_id,
      newTimecard.project.id
    );
    let newTimecardsDay = [...timecards];
    newTimecardsDay.splice(indexInsertDay, 0, newItem);

    setTimecards(newTimecardsDay);

    const indexInsertWeek = lastIndexOf(
      timecardsWeek,
      newTimecard.package.package_id,
      newTimecard.project.id
    );
    let newTimecardsWeek = [...timecardsWeek];
    newTimecardsWeek.splice(indexInsertWeek, 0, newItem);

    setTimecardsWeek(newTimecardsWeek);

    try {
      // 2.13.25 I modified this function because users were not getting notified when the data was not getting saved on the server
      // so I added a timeout function to the requestConfig.  Then I check for different error messages and display them to the user.  Then
      // I set the user back to blank so that it forcess the user to reselect their name thus reloading the current data from the server.

      const addedTimecard = await TimecardsAPI.addTimecardItem(
        newItem,
        auth.token
      );
      const serverTimecards = [addedTimecard]; // Wrap in array for compatibility
      Logger.info('Timecard added successfully', {
        timecard: serverTimecards[0],
      });
      if (addedTimecard?.id) {
        const resolvedFields = {
          location_type: addedTimecard.location_type,
          is_location_override: addedTimecard.is_location_override,
          phase: addedTimecard.phase,
          cost_code: addedTimecard.cost_code,
        };
        setTimecards((prev) =>
          prev.map((tc) =>
            tc.id === addedTimecard.id ? { ...tc, ...resolvedFields } : tc
          )
        );
        setTimecardsWeek((prev) =>
          prev.map((tc) =>
            tc.id === addedTimecard.id ? { ...tc, ...resolvedFields } : tc
          )
        );
      }
    } catch (err) {
      Logger.error('Error adding timecard', err);

      let errorMessage;

      if (err.code === 'ECONNABORTED') {
        errorMessage =
          'The server did not respond.  Please check your network connection and try again';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message?.includes('Network Error')) {
        errorMessage =
          'Could not connect to the server. Check your internet connection.';
      } else {
        errorMessage = 'An unknown error occured.';
      }

      dispatch(
        alertActions.showAlert({
          title: 'Error Adding Timecard',
          message:
            'The server encountered the following error while adding the timecard: ' +
            errorMessage,
        })
      );

      setTimeCardUser('');
      // setTimecards(oldTimecardsDay);
      // setTimecardsWeek(oldTimecardsWeek);
    }
  };

  // Fetch union information for an employee
  const fetchUnionInfo = async (employeeId) => {
    if (!employeeId) {
      setBaseUnion('');
      setTemporaryWorkingUnion(null);
      setEffectiveUnion('');
      setSessionUnion(null);
      setIsSessionOverride(false);
      return;
    }

    try {
      const unionInfo = await EmployeesAPI.getUnion(employeeId, auth.token);
      Logger.debug('Union info retrieved', { unionInfo });

      if (unionInfo) {
        const baseUnionValue = unionInfo.base_union || '';
        const tempUnionValue = unionInfo.temporary_working_union || null;
        // Use effective_union from backend (which is COALESCE(temporary_working_union, home_union))
        // Fallback to temporary_working_union if available, otherwise base_union
        const effectiveUnionValue =
          unionInfo.effective_union || tempUnionValue || baseUnionValue || '';

        // Batch all state updates together to prevent intermediate renders
        setBaseUnion(baseUnionValue);
        setTemporaryWorkingUnion(tempUnionValue);
        setEffectiveUnion(effectiveUnionValue);
      } else {
        console.warn(
          "Union info is null or undefined, using user's home_union"
        );
        // Fallback to user's home_union if available - check multiple sources
        const user =
          filteredUsers.find((u) => u.id === employeeId) ||
          users.find((u) => u.id === employeeId) ||
          timeCardUser?.id === employeeId
            ? timeCardUser
            : null;
        if (user?.home_union) {
          setBaseUnion(user.home_union);
          setEffectiveUnion(user.home_union);
        }
      }
      // Clear session override when employee changes
      setSessionUnion(null);
      setIsSessionOverride(false);
    } catch (err) {
      console.error('Error fetching union info: ', err);
      console.error('Error details:', err.response?.data);
      // Set defaults on error, but try to use user's home_union as fallback
      // Check multiple sources: filteredUsers, users array, and timeCardUser
      const user =
        filteredUsers.find((u) => u.id === employeeId) ||
        users.find((u) => u.id === employeeId) ||
        (timeCardUser?.id === employeeId ? timeCardUser : null);
      if (user?.home_union) {
        setBaseUnion(user.home_union);
        setEffectiveUnion(user.home_union);
        setTemporaryWorkingUnion(null);
      } else {
        setBaseUnion('');
        setTemporaryWorkingUnion(null);
        setEffectiveUnion('');
      }
    }
  };

  const handleUserWeekChange = async (newValue, itemChanged) => {
    Logger.userAction('User week changed', { itemChanged });

    let dateSelected;

    if (itemChanged === 'user') {
      setTimeCardUser(newValue);
      setIsLocationOverrideActive(false);
      setLocationOverrideConfirmOpen(false);
      setSessionUnion(null);
      setIsSessionOverride(false);
      // Set initial union from user object, then fetch full union info
      if (newValue?.id) {
        Logger.debug('User selected', {
          userId: newValue.id,
          homeUnion: newValue.home_union,
        });

        // Set initial union from user's home_union property
        // Check newValue first, then fallback to users array and filteredUsers
        const userFromUsers = users.find((u) => u.id === newValue.id);
        const userFromFiltered = filteredUsers.find(
          (u) => u.id === newValue.id
        );

        const userHomeUnion =
          newValue.home_union ||
          userFromUsers?.home_union ||
          userFromFiltered?.home_union;

        Logger.debug('Resolved user home union', { userHomeUnion });

        if (userHomeUnion) {
          setBaseUnion(userHomeUnion);
          setEffectiveUnion(userHomeUnion);
        }

        const resolvedIsShop =
          newValue.is_shop_location ??
          userFromUsers?.is_shop_location ??
          userFromFiltered?.is_shop_location;
        setEmployeeIsShopLocation(resolvedIsShop === true);

        // Fetch complete union info (including temporary overrides)
        await fetchUnionInfo(newValue.id);

        // Fetch packages filtered by employee location (shop vs field)
        try {
          const packages = await PackagesAPI.getActivePackages(
            newValue.id,
            auth.token
          );
          Logger.debug('Fetched filtered packages for employee', {
            employeeId: newValue.id,
            packageCount: packages.length,
          });
          setFilteredActivePackages(packages);
        } catch (err) {
          Logger.error('Error fetching filtered packages', err);
          // Fallback to all active packages if filtering fails
          setFilteredActivePackages(activePackages);
        }
      } else {
        // Clear union info if no user selected
        setBaseUnion('');
        setEffectiveUnion('');
        setTemporaryWorkingUnion(null);
        // No employee selected: chip is not clickable (ticket #246)
        setEmployeeIsShopLocation(false);
        // Reset to all packages when no user selected
        setFilteredActivePackages(activePackages);
      }
    } else if (itemChanged === 'foreman') {
      setTimeCardForeman(newValue);
    } else if (itemChanged === 'timeDate') {
      dateSelected = newValue;
      setTimeCardDate(format(new Date(dateSelected), 'yyyy/MM/dd'));

      // Clear session override when date changes
      setSessionUnion(null);
      setIsSessionOverride(false);
      // Clear the shop -> field location override on date change too (ticket #246)
      setIsLocationOverrideActive(false);
      setLocationOverrideConfirmOpen(false);

      if (
        (new Date(dateSelected) >= addDays(new Date(weekStart), 2)) &
        (new Date(dateSelected) <= new Date(weekEnd))
      ) {
        Logger.debug('Date is in current week');
        const currentTimeItems = timecardsWeek.filter(
          (timeItem) =>
            format(new Date(timeItem.date), 'yyyy/MM/dd') ===
            format(new Date(dateSelected), 'yyyy/MM/dd')
        );
        setTimecards(currentTimeItems);
      } else {
        Logger.debug('Setting new week for timecard', { dateSelected });
        setWeekStart(
          isSaturday(new Date(dateSelected)) || isSunday(new Date(dateSelected))
            ? format(
                previousFriday(subDays(new Date(dateSelected), 4)),
                'yyyy/MM/dd'
              )
            : format(previousFriday(new Date(dateSelected)), 'yyyy/MM/dd')
        );
        setWeekEnd(
          isSunday(new Date(dateSelected))
            ? format(new Date(dateSelected), 'yyyy/MM/dd')
            : format(nextSunday(new Date(dateSelected)), 'yyyy/MM/dd')
        );
      }
    }
  };

  useEffect(() => {
    if (!timeCardUser?.id) return;

    let cancelled = false;

    const refetchPackagesForOverride = async () => {
      try {
        const packages = await PackagesAPI.getActivePackages(
          timeCardUser.id,
          auth.token,
          isLocationOverrideActive
        );
        if (cancelled) return;
        Logger.debug('Re-fetched packages for override toggle', {
          employeeId: timeCardUser.id,
          overrideActive: isLocationOverrideActive,
          packageCount: packages.length,
        });
        setFilteredActivePackages(packages);
      } catch (err) {
        if (cancelled) return;
        Logger.error('Error re-fetching packages for override toggle', err);
        // Fallback to all active packages if filtering fails
        setFilteredActivePackages(activePackages);
      }
    };

    refetchPackagesForOverride();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocationOverrideActive]);

  const handleValueChange = async (e) => {
    Logger.debug('Timecard value changed', { field: e.field, row: e.row });
    const fieldChanged = e.field;
    let newValue;
    let changeValue = true;
    const itemId = e.id;
    const packageChanged = e.row.package_name;
    const activityChanged = e.row.activity_name;

    // Check if the timecard is locked
    if (e.row.is_locked) {
      dispatch(
        alertActions.showAlert({
          title: 'Cannot Edit Locked Timecard',
          message:
            'This timecard has been approved and is locked. Unapprove the week first to edit.',
        })
      );
      return;
    }

    if (fieldChanged !== 'notes') {
      const categoryChanged = fieldChanged.replace('hours_', '');
      newValue = parseFloat(e.value);

      // This is going to check if there is a value already in the time.  If so it will ask the user to confirm the change before making it.
      const oldValue = parseFloat(e.row[fieldChanged]);
      Logger.debug('Checking old value before change', {
        oldValue,
        fieldChanged,
      });
      if (oldValue !== 0) {
        changeValue = window.confirm(
          `Do you want to change ${packageChanged} ${activityChanged} ${categoryChanged} from ${oldValue} hours to ${newValue} hours?`
        );
      }
    } else {
      newValue = e.value;
    }

    const timecardUpdate = (time) => {
      const response = time.map((timecard) => {
        if (timecard.id === itemId) {
          return {
            ...timecard,
            [fieldChanged]: newValue,
          };
        } else {
          return timecard;
        }
      });
      return response;
    };

    if (changeValue) {
      const oldTimecard = [...timecards];
      const newTimecard = timecardUpdate(timecards);
      setTimecards(newTimecard);

      const oldTimecardsWeek = [...timecardsWeek];
      const newTimecardsWeek = timecardUpdate(timecardsWeek);
      setTimecardsWeek(newTimecardsWeek);

      let timecardResponse;

      const data = {
        itemId: itemId,
        fieldChanged: fieldChanged,
        newValue: newValue,
      };

      try {
        timecardResponse = await TimecardsAPI.updateTimecard(
          data.itemId,
          data.fieldChanged,
          data.newValue,
          auth.token
        );
        Logger.info('Timecard updated successfully', { timecardResponse });
      } catch (err) {
        if (err.response) {
          Logger.error('Error updating timecard', err);
        }

        // Check if error is related to locked timecard
        const errorMessage =
          err.response?.data?.message || err.message || 'Unknown error';
        const isLockedError = errorMessage.toLowerCase().includes('locked');

        dispatch(
          alertActions.showAlert({
            title: isLockedError
              ? 'Cannot Edit Locked Timecard'
              : 'Error Updating Timecard',
            message: isLockedError
              ? 'This timecard has been approved and is locked. Unapprove the week first to edit.'
              : 'The server encountered the following error while updating the timecard: ' +
                errorMessage,
          })
        );
        setTimecards(oldTimecard);
        setTimecardsWeek(oldTimecardsWeek);
      }
    } else {
      setTimecards([...timecards]);
    }
  };

  const handleEdit = (item) => {
    // Check if the timecard is locked
    if (item.is_locked) {
      dispatch(
        alertActions.showAlert({
          title: 'Cannot Edit Locked Timecard',
          message:
            'This timecard has been approved and is locked. Unapprove the week first to edit.',
        })
      );
      return;
    }
    setDialogType('edit');
    setCurrentTimeItem(item);
    setDialogOpen(true);
  };

  const handleEditNote = (item) => {
    Logger.userAction('Editing timecard note', { item });
    // Check if the timecard is locked
    if (item.is_locked) {
      dispatch(
        alertActions.showAlert({
          title: 'Cannot Edit Locked Timecard',
          message:
            'This timecard has been approved and is locked. Unapprove the week first to edit notes.',
        })
      );
      return;
    }
    setDialogType('notes');
    setCurrentTimeItem(item);
    setDialogOpen(true);
  };

  const submitEdit = async (changedItem) => {
    Logger.userAction('Submitting edited timecard', { changedItem });

    const oldTimecards = [...timecards];
    const newTimecards = timecards.map((timecard) => {
      if (timecard.id === changedItem.id) {
        return changedItem;
      } else {
        return timecard;
      }
    });
    setTimecards(newTimecards);

    const oldTimecardsWeek = [...timecardsWeek];

    const newTimecardsWeek = timecardsWeek.map((timecard) => {
      if (timecard.id === changedItem.id) {
        return changedItem;
      } else {
        return timecard;
      }
    });

    setTimecardsWeek(newTimecardsWeek);

    setDialogOpen(false);

    let timecardResponse;

    try {
      timecardResponse = await TimecardsAPI.updateTimecardAll(
        changedItem,
        auth.token
      );
      Logger.info('Timecard updated successfully', { timecardResponse });
    } catch (err) {
      if (err.response) {
        Logger.error('Error updating timecard', err);
      }

      // Check if error is related to locked timecard
      const errorMessage =
        err.response?.data?.message || err.message || 'Unknown error';
      const isLockedError = errorMessage.toLowerCase().includes('locked');

      dispatch(
        alertActions.showAlert({
          title: isLockedError
            ? 'Cannot Edit Locked Timecard'
            : 'Error Updating Timecard',
          message: isLockedError
            ? 'This timecard has been approved and is locked. Unapprove the week first to edit.'
            : 'The server encountered the following error while updating the timecard: ' +
              errorMessage,
        })
      );
      setTimecards(oldTimecards);
      setTimecardsWeek(oldTimecardsWeek);
    }

    setCurrentTimeItem({
      project_name: '',
      package_name: '',
      activity_name: '',
      hours_st: 0,
      hours_ot: 0,
      hours_dt: 0,
      exception: '',
    });
  };

  const handleConfirmDelete = (item) => {
    // Check if the timecard is locked
    if (item.is_locked) {
      dispatch(
        alertActions.showAlert({
          title: 'Cannot Delete Locked Timecard',
          message:
            'This timecard has been approved and is locked. Unapprove the week first to delete.',
        })
      );
      return;
    }
    setDialogType('delete');
    setCurrentTimeItem(item);
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    setDialogOpen(false);
    const deletedId = currentTimeItem.id;

    const timecardsBefore = timecards;
    const timecardsWeekBefore = timecardsWeek;

    setTimecards(timecards.filter((timecard) => timecard.id !== deletedId));
    setTimecardsWeek(
      timecardsWeek.filter((timecard) => timecard.id !== deletedId)
    );
    try {
      await TimecardsAPI.deleteTimecard(deletedId, auth.token);
      Logger.info('Timecard deleted successfully', { deletedId });
    } catch (err) {
      Logger.error('Error deleting timecard', err);

      // Check if error is related to locked timecard
      const errorMessage =
        err.response?.data?.message || err.message || 'Unknown error';
      const isLockedError = errorMessage.toLowerCase().includes('locked');

      dispatch(
        alertActions.showAlert({
          title: isLockedError
            ? 'Cannot Delete Locked Timecard'
            : 'Error Deleting Timecard',
          message: isLockedError
            ? 'This timecard has been approved and is locked. Unapprove the week first to delete.'
            : 'The server encountered the following error while deleting the timecard: ' +
              errorMessage,
        })
      );
      setTimecards(timecardsBefore);
      setTimecardsWeek(timecardsWeekBefore);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setCurrentTimeItem({
      project_name: '',
      package_name: '',
      activity_name: '',
      hours_st: 0,
      hours_ot: 0,
      hours_dt: 0,
      exception: '',
    });
  };

  const handleSupervision = async () => {
    const data = {
      user: timeCardUser,
      date: timeCardDate,
    };

    try {
      const result = await TimecardsAPI.addSupervision(data, auth.token);
      Logger.info('Supervision added successfully', {
        addedCount: result.addedTimecards?.length,
        remainingTime: result.remainingTime,
      });
      const { addedTimecards, remainingTime } = result;
      setTimecards([...timecards, ...addedTimecards]);
      setTimecardsWeek([...timecardsWeek, ...addedTimecards]);
      const responseMessage =
        remainingTime === 0
          ? 'All supervision hours were allocated to the items below.'
          : remainingTime > 0
            ? `Please note there was ${remainingTime} that were not allocated.  Please add this to one of the below items`
            : `Please note there was an extra ${
                -1 * remainingTime
              } allocated to the items below.  Please remove this amount from one of the items`;

      dispatch(
        alertActions.showAlert({
          title: 'Supervision Added',
          message: responseMessage,
        })
      );
    } catch (err) {
      Logger.error('Error adding supervision to timecard', err);
      dispatch(
        alertActions.showAlert({
          title: 'Error Adding Supervision',
          message:
            'The server encountered the following error while adding the supervision time: ' +
            err.response.data.message,
        })
      );
    }
  };

  const handleTestModalOpen = () => {
    if (!timeCardForeman) {
      return;
    }
    setTestModalOpen(true);
  };

  const handleEmploymentStatusModalOpen = () => {
    if (timeCardUser?.id) {
      setSelectedEmploymentStatus(
        timeCardUser.employment_status_id || 'active'
      );
      setEmploymentStatusChangeDate(new Date());
      // Pre-populate union input with current effective union
      const currentUnion = isSessionOverride ? sessionUnion : effectiveUnion;
      setUnionInputValue(currentUnion || '');
      // If there's a persistent override, default to persistent, otherwise date_only
      setUnionOverrideType(temporaryWorkingUnion ? 'persistent' : 'date_only');
      setEmploymentStatusModalOpen(true);
    }
  };

  const handleClearTemporaryOverride = async () => {
    if (!timeCardUser?.id) return;

    try {
      await EmployeesAPI.removeTemporaryUnion(timeCardUser.id, auth.token);
      // Refresh union info from backend to ensure state is in sync
      await fetchUnionInfo(timeCardUser.id);
      setEmploymentStatusModalOpen(false);
    } catch (err) {
      Logger.error('Error clearing temporary union override', err);
      dispatch(
        alertActions.showAlert({
          title: 'Error Clearing Union Override',
          message:
            'Failed to clear union override: ' +
            (err.response?.data?.message || err.message),
        })
      );
    }
  };

  // Calculate the union to display
  const getDisplayUnion = () => {
    if (isSessionOverride && sessionUnion) {
      return sessionUnion;
    }
    return effectiveUnion;
  };

  // Check if union override is active
  // Only show override if temporary union is different from base union
  const isUnionOverrideActive = () => {
    if (isSessionOverride) return true;
    // Only show override if temporary union exists AND is different from base union
    return (
      temporaryWorkingUnion !== null &&
      temporaryWorkingUnion !== '' &&
      temporaryWorkingUnion !== baseUnion
    );
  };

  // Get current employment status for display
  const getCurrentEmploymentStatus = () => {
    if (!timeCardUser) return 'Unknown';
    const statusId = timeCardUser.employment_status_id || 'active';
    const status = employmentStatuses.find((status) => status.id === statusId);
    if (!status && employmentStatuses.length > 0) {
      Logger.warn('Employment status not found', {
        statusId,
        user: timeCardUser,
      });
    }

    const statusName = status?.name || 'Unknown';
    const isActive = statusId === 'active';
    const lastChangeDate = timeCardUser.last_status_change_date;

    // Debug log to check if date is present
    if (!isActive) {
      Logger.debug('Inactive user detected', {
        statusName,
        lastChangeDate,
        user: timeCardUser,
      });
    }

    // Add date for inactive users
    if (!isActive && lastChangeDate) {
      const date = new Date(lastChangeDate);
      const formattedDate = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      return `${statusName} (${formattedDate})`;
    }

    return statusName;
  };

  const handleEmploymentStatusUpdate = async () => {
    if (!timeCardUser?.id || !selectedEmploymentStatus) return;

    // Save current selections before refreshing
    const currentForemanId = timeCardForeman?.id;
    const currentUserId = timeCardUser?.id;

    setIsUpdatingEmploymentStatus(true);

    // Store union value and type before potential reset
    // Only consider it an override if the value is different from current effective union
    const unionValue = unionInputValue.trim();
    const currentEffectiveUnion = isSessionOverride
      ? sessionUnion
      : effectiveUnion;
    const hasUnionOverride = unionValue && unionValue !== currentEffectiveUnion;
    const currentUnionOverrideType = unionOverrideType;

    // Store previous state for potential rollback
    const previousUserState = { ...timeCardUser };

    // Optimistically update the user's employment status immediately
    const updatedUser = {
      ...timeCardUser,
      employment_status_id: selectedEmploymentStatus,
      last_status_change_date: employmentStatusChangeDate
        ? employmentStatusChangeDate.toISOString()
        : null,
    };
    setTimeCardUser(updatedUser);

    // Close modal immediately for better UX
    setEmploymentStatusModalOpen(false);
    setUnionInputValue('');
    setUnionOverrideType('date_only');

    try {
      // Save union override if there's a value entered
      if (hasUnionOverride) {
        if (currentUnionOverrideType === 'persistent') {
          // Set temporary working union (persistent override)
          await EmployeesAPI.setTemporaryUnion(
            timeCardUser.id,
            unionValue,
            auth.token
          );
          // Refresh union info from backend to ensure state is in sync
          await fetchUnionInfo(timeCardUser.id);
        } else {
          // Set session override (date only)
          setSessionUnion(unionValue);
          setIsSessionOverride(true);
        }
      }

      // Save employment status
      await EmployeesAPI.updateEmploymentStatus(
        timeCardUser.id,
        {
          statusId: selectedEmploymentStatus,
          changedBy: auth.userId,
          notes: `Updated from Timecard tab`,
          changedDate: employmentStatusChangeDate
            ? format(employmentStatusChangeDate, 'yyyy-MM-dd') + ' 00:00:00'
            : null,
        },
        auth.token
      );

      // Refresh users to get updated status from backend
      const options = await TimecardsAPI.getTimecardOptions(auth.token);
      const usersSent = options.timecardUsers;
      setUsers(usersSent);

      // Find the updated user from backend
      const backendUser = usersSent.find((u) => u.id === currentUserId);

      // Merge backend data with optimistic update to ensure we keep the correct status
      // This handles cases where backend might have stale data or hasn't fully propagated
      const finalUser = backendUser
        ? {
            ...backendUser,
            // Preserve our optimistic update if backend status doesn't match yet
            employment_status_id:
              backendUser.employment_status_id === selectedEmploymentStatus
                ? backendUser.employment_status_id
                : selectedEmploymentStatus,
            last_status_change_date:
              backendUser.employment_status_id === selectedEmploymentStatus
                ? backendUser.last_status_change_date
                : employmentStatusChangeDate
                  ? employmentStatusChangeDate.toISOString()
                  : null,
          }
        : updatedUser; // Fallback to optimistic update if user not found

      // Store the final user data to use in restoration
      pendingStatusUpdateRef.current = {
        selectedStatus: selectedEmploymentStatus,
        changeDate: employmentStatusChangeDate
          ? employmentStatusChangeDate.toISOString()
          : null,
      };

      // Restore the selected foreman first
      if (currentForemanId) {
        const restoredForeman = usersSent.find(
          (u) => u.id === currentForemanId
        );
        if (restoredForeman) {
          setTimeCardForeman(restoredForeman);

          // Set pending user ID - the useEffect will restore it when filteredUsers is updated
          setPendingUserId(currentUserId);
        } else {
          // If foreman not found, clear it and restore user directly
          setTimeCardForeman('');
          setTimeCardUser(finalUser);
          setPendingUserId(null);
          setIsUpdatingEmploymentStatus(false);
        }
      } else {
        // No foreman selected, restore user directly from all users
        setTimeCardUser(finalUser);
        setPendingUserId(null);
        setIsUpdatingEmploymentStatus(false);
      }

      // Union info was already refreshed above if persistent override was set
    } catch (err) {
      Logger.error('Error updating employment status', err);
      // Clear the ref
      pendingStatusUpdateRef.current = null;
      // Revert optimistic update on error
      setTimeCardUser(previousUserState);
      setIsUpdatingEmploymentStatus(false);
      dispatch(
        alertActions.showAlert({
          title: 'Error',
          message: 'Failed to update employment status: ' + err.message,
        })
      );
    }
  };

  const handleTestModalClose = () => {
    setTestModalOpen(false);
  };

  const handleUpdate = async () => {
    if (dualListBoxRef.current) {
      try {
        setIsLoading(true);
        await dualListBoxRef.current.saveAssignments();

        // Trigger refresh of crew list
        setCrewRefreshTrigger((prev) => prev + 1);

        // Close the modal
        setTestModalOpen(false);
      } catch (err) {
        dispatch(
          alertActions.showAlert({
            title: 'Error Updating Crew Assignments',
            message:
              'There was an error updating the crew assignments: ' +
              err.message,
          })
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCopyPreviousEntry = async () => {
    if (!timeCardUser?.id) {
      return;
    }

    try {
      const previousDayData = await TimecardsAPI.getPreviousDayTimecard(
        timeCardUser.id,
        timeCardDate,
        auth.token
      );

      const previousTimecards = previousDayData.previousTimecards;

      if (previousTimecards && previousTimecards.length > 0) {
        // Process all entries from yesterday
        const itemsToAdd = [];

        for (const entry of previousTimecards) {
          // Find matching project, package, and activity objects
          const project = activeProjects.find((p) => p.id === entry.project_id);
          const packageItem = activePackages.find(
            (p) => p.package_id === entry.package_id
          );
          const activity = activityOptions.find(
            (a) => a.activity_id === entry.activity_id
          );

          // Only add if we can find all matching objects
          if (project && packageItem && activity) {
            // Create new timecard item with hours set to 0
            const newTimecard = {
              project: project,
              package: packageItem,
              activity: activity,
              hours_st: 0,
              hours_ot: 0,
              hours_dt: 0,
              shift: entry.shift || null,
              exception: entry.exception || '',
              payrollNotes: '', // Don't copy notes from previous day
            };

            itemsToAdd.push(newTimecard);
          }
        }

        if (itemsToAdd.length > 0) {
          // Add all items to the timecard
          for (const item of itemsToAdd) {
            await addTimeItem(item);
          }

          // Refresh timecard data to show the new entries
          try {
            const refreshData = await TimecardsAPI.getUserTimeRange(
              timeCardUser.id,
              weekStart,
              weekEnd,
              auth.token
            );
            const refreshedTimecards = refreshData.timecard_items;
            setTimecardsWeek(refreshedTimecards);

            const currentTimeItems = refreshedTimecards.filter(
              (timeItem) =>
                format(new Date(timeItem.date), 'yyyy/MM/dd') === timeCardDate
            );
            setTimecards(currentTimeItems);
          } catch (refreshErr) {
            Logger.error('Error refreshing timecards after copy', refreshErr);
          }
        } else {
          dispatch(
            alertActions.showAlert({
              title: 'No Valid Entries',
              message:
                "Could not find matching projects, packages, or activities for the previous day's entries.",
            })
          );
        }
      } else {
        dispatch(
          alertActions.showAlert({
            title: 'No Previous Entry',
            message: 'No timecard entries found for the previous day.',
          })
        );
      }
    } catch (err) {
      Logger.error('Error getting previous day timecard', err);
      dispatch(
        alertActions.showAlert({
          title: 'Error',
          message:
            'There was an error retrieving the previous day timecard: ' +
            err.message,
        })
      );
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100vw',
        height: '100%',
        paddingTop: 2,
        paddingLeft: { xs: 2, md: 3 },
        paddingRight: { xs: 2, md: 3 },
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <AlertDialog />
      {/* Shop -> field override confirmation (ticket #246). The shared redux
          AlertDialog only renders a single "Ok", so this local Dialog provides
          the Continue/Cancel choice needed to confirm turning the override ON. */}
      <Dialog
        open={locationOverrideConfirmOpen}
        onClose={() => setLocationOverrideConfirmOpen(false)}
        aria-labelledby="location-override-dialog-title"
      >
        <DialogTitle id="location-override-dialog-title">
          Temporary Field Override
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will charge today&apos;s eligible packages to field cost codes
            instead of shop for this employee. It resets automatically when you
            change employees, change the date, or leave this page. Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocationOverrideConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setIsLocationOverrideActive(true);
              setLocationOverrideConfirmOpen(false);
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          paddingTop: 2,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          minWidth: 0, // Allows container to shrink
          overflow: 'hidden', // Prevents content from overflowing
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            minWidth: { xs: '100%', md: 300 },
            maxWidth: { xs: '100%', md: 350 },
            marginRight: { xs: 0, md: 3 },
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel id="foreman-label">Foreman</InputLabel>
              <Select
                labelId="foreman-label"
                id="foreman"
                value={timeCardForeman}
                name="foreman"
                label="Foreman"
                onChange={(e) => {
                  handleUserWeekChange(e.target.value, e.target.name);
                }}
                sx={{ height: 50 }}
              >
                {users
                  .filter(
                    (user) =>
                      user.is_timecard === true &&
                      (user.employment_status_id === 'active' ||
                        !user.employment_status_id)
                  )
                  .map((user) => (
                    <MenuItem key={user.id} value={user}>
                      {`${user.firstname} ${user.lastname}`}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <Tooltip title="Edit Foreman's Crew">
              <span>
                <IconButton
                  color="primary"
                  onClick={handleTestModalOpen}
                  disabled={!timeCardForeman}
                  sx={{
                    height: 50,
                    width: 50,
                    flexShrink: 0,
                  }}
                  aria-label="Assign workers to crew"
                >
                  <GroupAddIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              alignItems: 'flex-start',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flex: 1,
                minWidth: 0,
              }}
            >
              <FormControl sx={{ minWidth: 200, maxWidth: 350, width: '100%' }}>
                <InputLabel id="user-label">Employee</InputLabel>
                <Select
                  labelId="user-label"
                  id="user"
                  value={(() => {
                    if (!timeCardUser) return '';
                    const matchingUser = filteredUsers.find(
                      (u) => u.id === timeCardUser.id
                    );
                    return matchingUser || '';
                  })()}
                  name="user"
                  label="Employee"
                  onChange={(e) => {
                    handleUserWeekChange(e.target.value, e.target.name);
                  }}
                  sx={{ height: 50 }}
                >
                  {filteredUsers.map((user) => (
                    <MenuItem key={user.id} value={user}>
                      {`${user.firstname} ${user.lastname}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {timeCardUser && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      minHeight: 50,
                      justifyContent: 'center',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'pointer',
                      }}
                      onClick={handleEmploymentStatusModalOpen}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isUnionOverrideActive()
                            ? 'bold'
                            : 'normal',
                          color: isUnionOverrideActive()
                            ? '#ff9800'
                            : 'text.primary',
                        }}
                      >
                        {getDisplayUnion() || 'N/A'}
                      </Typography>
                      {isUnionOverrideActive() && (
                        <WarningIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                      )}
                    </Box>
                    {/* Location chip (ticket #246): always shown for a selected
                        employee; clickable only for shop-based employees to
                        toggle the temporary shop -> field override. */}
                    <Chip
                      label={
                        isLocationOverrideActive
                          ? 'FIELD (OVERRIDE)'
                          : employeeIsShopLocation
                            ? 'SHOP'
                            : 'FIELD'
                      }
                      size="small"
                      color={
                        isLocationOverrideActive || !employeeIsShopLocation
                          ? 'primary'
                          : 'warning'
                      }
                      onClick={
                        employeeIsShopLocation
                          ? () => {
                              if (!isLocationOverrideActive) {
                                // Confirm before turning the override ON
                                setLocationOverrideConfirmOpen(true);
                              } else {
                                // Turning it off needs no confirmation
                                setIsLocationOverrideActive(false);
                              }
                            }
                          : undefined
                      }
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                        height: 22,
                        cursor: employeeIsShopLocation ? 'pointer' : 'default',
                      }}
                    />
                  </Box>
                  <Tooltip
                    title={`Update Employment Status - Current: ${getCurrentEmploymentStatus()}`}
                  >
                    <IconButton
                      onClick={handleEmploymentStatusModalOpen}
                      sx={{
                        height: 50,
                        width: 50,
                        flexShrink: 0,
                        color:
                          (timeCardUser.employment_status_id || 'active') ===
                          'active'
                            ? '#4caf50'
                            : '#d32f2f',
                      }}
                    >
                      {(timeCardUser.employment_status_id || 'active') ===
                      'active' ? (
                        <CheckCircleIcon />
                      ) : (
                        <CancelIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                </>
              )}
              {!timeCardUser && (
                <Box sx={{ width: 50, height: 50, flexShrink: 0 }} />
              )}
            </Box>
          </Box>
          <Box>
            <LocalizationProvider name="timeDate" dateAdapter={AdapterDateFns}>
              <Box
                sx={{
                  width: '100%',
                  display: { xs: 'block', md: 'none' },
                }}
              >
                <DatePicker
                  label="Timecard Date"
                  value={timeCardDate}
                  onChange={(newValue) => {
                    handleUserWeekChange(newValue, 'timeDate');
                  }}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  sx={{ height: 50, width: '100%' }}
                />
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <StaticDatePicker
                  displayStaticWrapperAs="desktop"
                  openTo="day"
                  value={timeCardDate}
                  size="small"
                  name="timeDate"
                  onChange={(newValue) => {
                    handleUserWeekChange(newValue, 'timeDate');
                  }}
                  renderInput={(params) => <TextField {...params} />}
                />
              </Box>
            </LocalizationProvider>
          </Box>
        </Box>
        {timeCardUser && (
          <Box
            sx={{
              flex: { xs: '1 1 100%', md: '1 1 auto' },
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              paddingLeft: { xs: 0, md: 2 },
              paddingTop: { xs: 2, md: 0 },
              minWidth: 0, // Allows flex item to shrink below content size
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', md: '900px' },
                minWidth: 0, // Allows content to shrink
              }}
            >
              <TimeCardAddItem
                activityOptions={activityOptions}
                activeProjects={activeProjects}
                activePackages={filteredActivePackages}
                areas={areas}
                addTimeItem={addTimeItem}
                addTimeItems={addTimeItems}
                handleSupervision={handleSupervision}
                isAdmin={auth.isAdmin}
                setAreas={setAreas}
                timeCardUser={timeCardUser}
                timeCardForeman={timeCardForeman}
                timeCardDate={timeCardDate}
              />
            </Box>
          </Box>
        )}
      </Box>
      {timeCardUser ? (
        <Box
          sx={{
            width: '100%',
            paddingLeft: { xs: 0, md: 0 },
            paddingRight: { xs: 0, md: 0 },
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: '100%',
              paddingBottom: { xs: 0, md: 15 },
              boxSizing: 'border-box',
              overflow: 'hidden',
            }}
          >
            {isLoading ? (
              <LinearProgress />
            ) : (
              <Box>
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                  <DataGrid
                    columnVisibilityModel={{
                      work_item: false,
                    }}
                    getRowId={(row) => row.id}
                    rows={timecards}
                    columns={columns}
                    onCellEditCommit={handleValueChange}
                    pageSize={20}
                    rowsPerPageOptions={[20]}
                    components={{ Footer: CustomFooter }}
                    autoHeight
                    getRowClassName={(params) => {
                      let className = '';
                      if (params.row.is_locked) {
                        className += 'locked-row ';
                      }
                      if (params.row.is_union_override) {
                        className += 'union-override-row';
                      }
                      return className.trim();
                    }}
                    sx={{
                      '& .locked-row': {
                        backgroundColor: 'rgba(0,0,0,0.05)',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.08)',
                        },
                      },
                      '& .union-override-row': {
                        backgroundColor: '#fff3e0',
                        '&:hover': {
                          backgroundColor: '#ffe0b2',
                        },
                      },
                      // Locked rows take precedence over union override styling
                      '& .locked-row.union-override-row': {
                        backgroundColor: 'rgba(0,0,0,0.05)',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.08)',
                        },
                      },
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    display: { xs: 'block', md: 'none' },
                  }}
                  color="text.secondary"
                  align="right"
                >
                  Total Hours:{'  '}
                  {timecards.reduce((accumulator, object) => {
                    return accumulator + object.hours_st;
                  }, 0)}
                  {'  '}
                  ST{'  '}
                  {timecards.reduce((accumulator, object) => {
                    return accumulator + object.hours_ot;
                  }, 0)}
                  {'  '}
                  OT{'  '}
                  {timecards.reduce((accumulator, object) => {
                    return accumulator + object.hours_dt;
                  }, 0)}
                  {'  '}
                  DT
                </Typography>
                <Box
                  sx={{
                    display: { xs: 'block', md: 'none' },
                    height: '40vh',
                    overflowY: 'scroll',
                    background: '#f5f5f5',
                  }}
                >
                  <TimecardDialog
                    open={dialogOpen}
                    variant={dialogType}
                    timeItem={currentTimeItem}
                    handleDialogClose={handleDialogClose}
                    handleDelete={handleDelete}
                    submitEdit={submitEdit}
                  />
                  {timecards.map((timecard) => (
                    <TimecardItem
                      key={timecard.id}
                      timeItem={timecard}
                      handleDelete={handleConfirmDelete}
                      handleEdit={handleEdit}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      ) : (
        ''
      )}
      <Dialog
        open={testModalOpen}
        onClose={handleTestModalClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            Assign Workers to{' '}
            {timeCardForeman
              ? `${timeCardForeman.firstname} ${timeCardForeman.lastname}`
              : 'Foreman'}
            <IconButton
              aria-label="close"
              onClick={handleTestModalClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DualListBox
            ref={dualListBoxRef}
            selectedForeman={timeCardForeman}
            authToken={auth.token}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={handleUpdate}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={employmentStatusModalOpen}
        onClose={() => {
          setEmploymentStatusModalOpen(false);
          setUnionInputValue('');
          setUnionOverrideType('date_only');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography variant="h6">
                Employee: {timeCardUser?.firstname} {timeCardUser?.lastname}
              </Typography>
            </Box>
            <IconButton
              aria-label="close"
              onClick={() => setEmploymentStatusModalOpen(false)}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {/* Union Override Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Override Local Pay Union
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Base Union: <strong>{baseUnion || 'N/A'}</strong>
                </Typography>
                {temporaryWorkingUnion && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Current Override: <strong>{temporaryWorkingUnion}</strong>
                  </Typography>
                )}
              </Alert>

              <TextField
                fullWidth
                label="Local Pay Union"
                value={unionInputValue}
                onChange={(e) => setUnionInputValue(e.target.value)}
                sx={{ mb: 2 }}
                placeholder="Enter union name"
              />

              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <FormLabel component="legend">Override Duration</FormLabel>
                <RadioGroup
                  value={unionOverrideType}
                  onChange={(e) => setUnionOverrideType(e.target.value)}
                >
                  <FormControlLabel
                    value="date_only"
                    control={<Radio />}
                    label={`Override for ${format(
                      new Date(timeCardDate.replace(/\//g, '-')),
                      'MM/dd/yyyy'
                    )} only`}
                  />
                  <FormControlLabel
                    value="persistent"
                    control={<Radio />}
                    label="Hold Override for All Future Timecards until updated"
                  />
                </RadioGroup>
              </FormControl>

              {unionOverrideType === 'persistent' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This override will remain in effect until you clear it. The
                  employee will use this union for all future time entries.
                </Alert>
              )}

              {temporaryWorkingUnion && (
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  onClick={handleClearTemporaryOverride}
                  sx={{ mb: 2 }}
                >
                  Clear Override
                </Button>
              )}
            </Box>

            {/* Employment Status Section */}
            <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Update Employment Status
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="employment-status-select-label">
                  Employment Status
                </InputLabel>
                <Select
                  labelId="employment-status-select-label"
                  id="employment_status"
                  value={selectedEmploymentStatus}
                  label="Employment Status"
                  onChange={(e) => setSelectedEmploymentStatus(e.target.value)}
                >
                  {employmentStatuses.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      {status.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Effective Date"
                  value={employmentStatusChangeDate}
                  onChange={(newValue) => {
                    setEmploymentStatusChangeDate(newValue);
                  }}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              setEmploymentStatusModalOpen(false);
              setUnionInputValue('');
              setUnionOverrideType('date_only');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleEmploymentStatusUpdate}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <CopyPreviousEntryDialog
        open={copyPreviousEntryModalOpen}
        onClose={() => {
          setCopyPreviousEntryModalOpen(false);
        }}
        onYes={() => {
          handleCopyPreviousEntry();
          setCopyPreviousEntryModalOpen(false);
        }}
        onNo={() => {
          setCopyPreviousEntryModalOpen(false);
        }}
      />
    </Box>
  );
};
